/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-27 — the web side of dictation, and the ONLY file that knows the plugin
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The native half lives in the tablet app
 * (`android/app/src/main/java/cc/flowerworld/kiosk/VoskDictationPlugin.java`).
 * This is the seam between it and the React code: features ask "can this device
 * hear?" and "start listening in Spanish", never `window.Capacitor`.
 *
 * The house rule (G-27 interaction rule 5, and the owner's 2026-08-04
 * constraint): outside the tablet app there is no recogniser, so `supported()`
 * is false and the microphone affordance is ABSENT — never a dead button, never
 * a broken one. The keyboard keeps working for everybody, and the CRM stays
 * fully usable as a plain web page, which owners and managers use daily.
 *
 * There is deliberately no browser fallback. The Web Speech API streams audio to
 * Google's servers, which OD-11 rejected outright; a desktop user who wants to
 * dictate has their operating system's own dictation, which is better than
 * anything we would ship and already installed.
 */

import { t } from "../i18n/t";
import { isNativeShell } from "./shell";

/**
 * The languages a florist can dictate in, in their own words rather than in
 * English (G-27 interaction rule 2).
 *
 * Lives here rather than in the component because the list is a property of
 * WHICH MODELS ARE IN THE APK — the native side rejects anything else — and two
 * copies of that answer would drift the first time a language is added.
 */
export const DICTATION_LOCALES = [
  { code: "es-ES", label: "Español" },
  { code: "ca-ES", label: "Català" },
  { code: "en-GB", label: "English" },
  { code: "ru-RU", label: "Русский" },
  { code: "uk-UA", label: "Українська" },
] as const;

export type DictationLocale = (typeof DICTATION_LOCALES)[number]["code"];

export function isDictationLocale(value: string): value is DictationLocale {
  return DICTATION_LOCALES.some((l) => l.code === value);
}

/** What the recogniser reports about itself. */
export interface DictationAvailability {
  supported: boolean;
  /** Whether the microphone has been granted. Asking for it is `start`'s job. */
  granted: boolean;
}

/** A listener handle, shaped like Capacitor's own so callers can just remove it. */
export interface DictationListener {
  remove(): Promise<void>;
}

interface VoskDictationPlugin {
  isAvailable(): Promise<{
    supported: boolean;
    granted: boolean;
    locales: Record<string, boolean>;
  }>;
  start(options: { locale: DictationLocale }): Promise<void>;
  stop(): Promise<void>;
  cancel(): Promise<void>;
  addListener(
    event: "partial" | "result",
    handler: (data: { text: string }) => void,
  ): Promise<DictationListener>;
  addListener(
    event: "error",
    handler: (data: { code?: string; message?: string }) => void,
  ): Promise<DictationListener>;
  addListener(
    event: "stopped",
    handler: (data: Record<string, never>) => void,
  ): Promise<DictationListener>;
}

function plugin(): VoskDictationPlugin | undefined {
  if (typeof window === "undefined") return undefined;
  const capacitor = (
    window as Window & { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  return capacitor?.Plugins?.VoskDictation as VoskDictationPlugin | undefined;
}

/** True only where speech can actually be recognised: inside the tablet app. */
export function dictationSupported(): boolean {
  return isNativeShell() && plugin() !== undefined;
}

/**
 * Whether the microphone is usable RIGHT NOW. Separate from
 * `dictationSupported` for the reason NFC separates its two checks: "this is a
 * desktop browser, there is no recogniser" and "the microphone has not been
 * allowed yet" need different words in front of a florist, and only one of them
 * is fixable at the counter.
 */
export async function dictationStatus(): Promise<DictationAvailability> {
  const p = plugin();
  if (!isNativeShell() || !p) return { supported: false, granted: false };
  try {
    const result = await p.isAvailable();
    return { supported: result.supported, granted: result.granted };
  } catch {
    return { supported: false, granted: false };
  }
}

/** Codes the native side rejects or reports with. Anything else is unexpected. */
export type DictationErrorCode =
  | "INVALID_LOCALE"
  | "BUSY"
  | "PERMISSION_DENIED"
  | "MODEL_UNAVAILABLE"
  | "AUDIO_ERROR"
  | "INTERRUPTED";

export function dictationErrorCode(error: unknown): DictationErrorCode | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" ? (code as DictationErrorCode) : null;
}

/**
 * Words for a florist, not for a developer.
 *
 * Every one of these names something the person holding the tablet can either
 * act on or safely ignore. None of them says "Vosk", "model" or "recogniser" —
 * a florist with wet hands is not debugging a speech engine.
 */
export function dictationErrorMessage(error: unknown): string {
  switch (dictationErrorCode(error)) {
    case "PERMISSION_DENIED":
      return t("dictation.permissionDenied");
    case "MODEL_UNAVAILABLE":
      return t("dictation.modelUnavailable");
    case "AUDIO_ERROR":
      return t("dictation.audioError");
    case "BUSY":
      return t("dictation.busy");
    case "INTERRUPTED":
      return t("dictation.interrupted");
    case "INVALID_LOCALE":
      // Unreachable from the UI — the chip only offers what the plugin accepts.
      // Kept because a silent empty string is worse than an odd sentence.
      return t("dictation.invalidLocale");
    default:
      return t("dictation.notWorking");
  }
}

/** Opens the microphone. Resolves once it is LIVE; words arrive as events. */
export async function startDictation(locale: DictationLocale): Promise<void> {
  const p = plugin();
  if (!p) throw new Error(t("dictation.unavailable"));
  await p.start({ locale });
}

/** Stops and keeps what was heard — the florist tapping the microphone again. */
export async function stopDictation(): Promise<void> {
  await plugin()?.stop();
}

/**
 * Stops and throws away what was heard.
 *
 * What effect cleanup calls. A final transcript arriving after the screen has
 * gone would be appended to a field nobody is looking at — and, worse, the
 * microphone would stay open, which the tablet shows as a live indicator for the
 * rest of the shift.
 */
export async function cancelDictation(): Promise<void> {
  await plugin()?.cancel();
}

export async function onDictationPartial(
  handler: (text: string) => void,
): Promise<DictationListener | undefined> {
  return plugin()?.addListener("partial", (data) => handler(data.text));
}

export async function onDictationResult(
  handler: (text: string) => void,
): Promise<DictationListener | undefined> {
  return plugin()?.addListener("result", (data) => handler(data.text));
}

export async function onDictationError(
  handler: (error: { code?: string; message?: string }) => void,
): Promise<DictationListener | undefined> {
  return plugin()?.addListener("error", handler);
}

/**
 * The recogniser stopped on its OWN — Vosk's end-of-speech timeout, not a tap.
 *
 * Subscribing to this is not optional. `/code-review` 2026-09-12 found the
 * native side emitting it with nobody listening, which left the button showing
 * its live accent fill and "Listening…" while the microphone was already shut.
 * On a shared tablet, whether the thing is recording is the one state the UI
 * must never get wrong.
 */
export async function onDictationStopped(
  handler: () => void,
): Promise<DictationListener | undefined> {
  return plugin()?.addListener("stopped", () => handler());
}
